(function () {
  "use strict";

  var SELECTORS = {
    header: ".custom-header",
    hoverZone: ".custom-header__hover-zone",
    menuItems: ".custom-header__menu-item",
    megaMenu: ".custom-header__mega-menu",
  };

  var CLASSES = {
    hidden: "ch-hidden",
    hoverReveal: "ch-hover-reveal",
    scrolled: "ch-scrolled",
    lightBg: "ch-light-bg",
    anyScroll: "ch-any-scroll",
  };

  var header = null;
  var hoverZone = null;
  var lastScrollY = 0;
  var scrollThreshold = 100;
  var ticking = false;
  var isHidden = false;
  var isHoverRevealed = false;

  /**
   * Initialize the custom header scroll behavior
   */
  function init() {
    header = document.querySelector(SELECTORS.header);
    hoverZone = document.querySelector(SELECTORS.hoverZone);

    if (!header) return;

    // Read threshold from data attribute
    var thresholdAttr = header.getAttribute("data-scroll-threshold");
    if (thresholdAttr) {
      scrollThreshold = parseInt(thresholdAttr, 10) || 100;
    }

    lastScrollY = window.scrollY;

    // Initial sync: if user lands already scrolled, apply "any scroll" look immediately.
    if (window.scrollY > 0) {
      header.classList.add(CLASSES.anyScroll);
      header.classList.remove(CLASSES.lightBg);
    }

    // Bind scroll listener
    window.addEventListener("scroll", onScroll, { passive: true });

    // Keep the header legible when it sits transparent over a light section
    updateContrast();
    window.addEventListener("resize", updateContrast, { passive: true });
    window.addEventListener("load", updateContrast);
    // Hero photos decode / lay out a beat after DOM-ready — re-sample a
    // couple of times so an early wrong guess doesn't stick ("sometimes black").
    setTimeout(updateContrast, 250);
    setTimeout(updateContrast, 1000);
    setTimeout(updateContrast, 2500);

    // Back-forward cache restore (browser Back) fires no load/scroll event, so
    // a stale `ch-light-bg` from the previous view can stick. Re-probe on show.
    window.addEventListener("pageshow", function () {
      if (window.scrollY <= 0) updateContrast();
    });

    // The probe returns the wrong answer while the hero image/video behind the
    // bar is still 0-sized or undecoded — it falls through to the (white) page
    // background and wrongly flips links to dark. Re-run the moment that media
    // is actually ready.
    watchBackdropMedia();

    // Bind hover zone listeners
    if (hoverZone) {
      hoverZone.addEventListener("mouseenter", onHoverZoneEnter);
    }

    // Also listen to header mouseenter/mouseleave for the hover reveal
    header.addEventListener("mouseenter", onHeaderEnter);
    header.addEventListener("mouseleave", onHeaderLeave);

    // Initialize mega menu hover behavior
    initMegaMenus();

    // Initialize the mobile drawer
    initDrawer();

    // Wire the search modal + cart drawer triggers and keep the cart badge in sync
    initActions();
  }

  /**
   * Search icon  -> opens the global <dialog-component id="search-modal">
   * Cart icon    -> opens the global <cart-drawer-component> (custom cart drawer)
   * Cart badge   -> mirrors cart:update events so the count stays live
   */
  function initActions() {
    var searchBtn = document.querySelector("[data-ch-search-open]");
    if (searchBtn) {
      searchBtn.addEventListener("click", function (event) {
        var modal = document.getElementById("search-modal");
        if (modal && typeof modal.showDialog === "function") {
          event.preventDefault();
          modal.showDialog();
        }
        // else: no JS modal available yet — let any default behaviour proceed
      });
    }

    var cartBtn = document.querySelector("[data-ch-cart-open]");
    if (cartBtn) {
      cartBtn.addEventListener("click", function (event) {
        var drawer = document.querySelector("cart-drawer-component");
        if (drawer && typeof drawer.open === "function") {
          event.preventDefault();
          drawer.open();
        }
        // else: fall through to the href="/cart" fallback
      });
    }

    // Keep the header cart badge current when the cart changes anywhere on the page
    document.addEventListener("cart:update", function (event) {
      // NB: [data-ch-cart-count], not `.custom-header__cart-badge` — the
      // favourites badge reuses that class and is the first match.
      var badges = document.querySelectorAll("[data-ch-cart-count]");
      if (!badges.length) return;

      var detail = (event && event.detail && event.detail.data) || {};
      var resource = (event && event.detail && event.detail.resource) || null;
      var current =
        parseInt(badges[0].getAttribute("data-count"), 10) || 0;
      var count = current;

      if (resource && typeof resource.item_count === "number") {
        // authoritative: the returned cart object
        count = resource.item_count;
      } else if (typeof detail.itemCount === "number") {
        // add-to-cart sends the delta added, not the new total
        count =
          detail.source === "product-form-component"
            ? current + detail.itemCount
            : detail.itemCount;
      }

      for (var i = 0; i < badges.length; i++) setCartBadge(badges[i], count);
    });
  }

  /**
   * @param {HTMLElement} badge
   * @param {number} count
   */
  function setCartBadge(badge, count) {
    if (count < 0 || isNaN(count)) count = 0;
    badge.setAttribute("data-count", String(count));
    badge.textContent = count > 0 ? (count < 100 ? String(count) : "99+") : "";
  }

  /**
   * Mobile drawer — open/close, accordions, scroll lock
   */
  function initDrawer() {
    var drawer = document.querySelector(".custom-header__drawer");
    var backdrop = document.querySelector(".custom-header__backdrop");
    var openBtn = document.querySelector("[data-ch-drawer-open]");

    if (!drawer || !openBtn) return;

    function openDrawer() {
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      openBtn.setAttribute("aria-expanded", "true");
      if (backdrop) {
        backdrop.hidden = false;
        // next frame so the opacity transition actually runs
        window.requestAnimationFrame(function () {
          backdrop.classList.add("is-open");
        });
      }
      document.body.style.overflow = "hidden";
      var firstLink = drawer.querySelector(".custom-header__drawer-close");
      if (firstLink) firstLink.focus();
    }

    function closeDrawer() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      openBtn.setAttribute("aria-expanded", "false");
      if (backdrop) {
        backdrop.classList.remove("is-open");
        setTimeout(function () {
          if (!drawer.classList.contains("is-open")) backdrop.hidden = true;
        }, 300);
      }
      document.body.style.overflow = "";
    }

    openBtn.addEventListener("click", openDrawer);

    Array.prototype.forEach.call(
      document.querySelectorAll("[data-ch-drawer-close]"),
      function (el) {
        el.addEventListener("click", closeDrawer);
      }
    );

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && drawer.classList.contains("is-open"))
        closeDrawer();
    });

    // Close when a destination is chosen — but NOT the parent links that only
    // open a submenu (Women / Men …); those must keep the drawer open.
    Array.prototype.forEach.call(
      drawer.querySelectorAll(
        "a[href]:not(.custom-header__drawer-link--with-toggle)"
      ),
      function (link) {
        link.addEventListener("click", closeDrawer);
      }
    );

    // Accordion submenus — animated open / close
    function toggleAccordion(toggle) {
      var row = toggle.closest(".custom-header__drawer-row");
      var panel = row ? row.nextElementSibling : toggle.nextElementSibling;
      var isOpen = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", isOpen ? "false" : "true");
      if (!panel) return;

      var reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // cancel any in-flight transition
      panel._chEnd && panel.removeEventListener("transitionend", panel._chEnd);

      if (isOpen) {
        // ---- close ----
        if (reduce) {
          panel.hidden = true;
          panel.style.height = "";
          return;
        }
        panel.style.height = panel.scrollHeight + "px";
        // force reflow so the height is committed before we shrink it
        void panel.offsetHeight;
        panel.style.height = "0px";
        panel._chEnd = function () {
          panel.hidden = true;
          panel.style.height = "";
          panel.removeEventListener("transitionend", panel._chEnd);
          panel._chEnd = null;
        };
        panel.addEventListener("transitionend", panel._chEnd);
      } else {
        // ---- open ----
        panel.hidden = false;
        if (reduce) {
          panel.style.height = "";
          return;
        }
        var target = panel.scrollHeight;
        panel.style.height = "0px";
        void panel.offsetHeight;
        panel.style.height = target + "px";
        panel._chEnd = function () {
          panel.style.height = ""; // let it be auto once open
          panel.removeEventListener("transitionend", panel._chEnd);
          panel._chEnd = null;
        };
        panel.addEventListener("transitionend", panel._chEnd);
      }
    }

    Array.prototype.forEach.call(
      drawer.querySelectorAll("[data-ch-accordion]"),
      function (toggle) {
        toggle.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggleAccordion(toggle);
        });

        // Tapping the parent label itself (Women / Men …) opens the submenu
        // instead of navigating to the category page.
        var row = toggle.closest(".custom-header__drawer-row");
        var parentLink = row
          ? row.querySelector(".custom-header__drawer-link--with-toggle")
          : null;
        if (parentLink) {
          parentLink.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            toggleAccordion(toggle);
          });
        }
      }
    );

    // Never leave the drawer stranded open when resizing up to desktop
    window.addEventListener("resize", function () {
      if (window.innerWidth > 749 && drawer.classList.contains("is-open"))
        closeDrawer();
    });
  }

  /**
   * Scroll handler — determines direction and toggles hidden state
   */
  function onScroll() {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(function () {
      var currentScrollY = window.scrollY;

      // === ANY scroll at all (even 1px) → alt logo, dark text, FAFAFA icon circles ===
      if (currentScrollY > 0) {
        header.classList.add(CLASSES.anyScroll);
        // Also drop lightBg (white/contrast probe) when scrolled even slightly —
        // the scrolled look has explicit colours of its own.
        header.classList.remove(CLASSES.lightBg);
      } else {
        header.classList.remove(CLASSES.anyScroll);
        // Back at top → re-sample contrast so hero overlay state is correct.
        updateContrast();
      }

      if (currentScrollY > scrollThreshold) {
        // Past the threshold — carry the dark glass background, and
        // hide/reveal based on scroll direction (2px dead zone kills jitter).
        header.classList.add(CLASSES.scrolled);
        // The glass background handles contrast now — drop the light-bg override.
        header.classList.remove(CLASSES.lightBg);

        if (currentScrollY > lastScrollY + 2) {
          // Scrolling DOWN — hide header
          hideHeader();
        } else if (currentScrollY < lastScrollY - 2) {
          // Scrolling UP — slide header back in
          showHeader();
        }
      } else {
        // Near the top — transparent and always visible
        header.classList.remove(CLASSES.scrolled);
        showHeader();
      }

      lastScrollY = currentScrollY;
      ticking = false;
    });
  }

  /**
   * Re-sample contrast whenever a media element sitting under the top of the
   * page finishes loading. Fixes the "links go black at scroll 0" flash where
   * the probe ran before the hero photo had a size.
   */
  function watchBackdropMedia() {
    var media = document.querySelectorAll(
      "img:not(.custom-header img), video:not(.custom-header video)"
    );
    for (var i = 0; i < media.length; i++) {
      var m = media[i];
      var r = m.getBoundingClientRect();
      // Only care about media that overlaps the first ~1.5 header-heights.
      if (r.top > (header.offsetHeight || 60) * 1.5) continue;
      if (m.tagName === "IMG" && m.complete && m.naturalWidth > 0) continue;
      var handler = function () {
        if (window.scrollY <= 0) updateContrast();
      };
      m.addEventListener("load", handler, { once: true });
      m.addEventListener("loadeddata", handler, { once: true });
    }
  }

  /**
   * Adaptive contrast — when the bar is transparent (near the top), look at
   * whatever section is sitting directly behind it. If that section has a
   * light background, add `ch-light-bg` so the CSS can swap the logo / links /
   * icons to dark ink. Over a dark section or a background image/video we keep
   * the default light treatment.
   */
  function updateContrast() {
    if (!header) return;

    // Only relevant while the bar is see-through (the glass state has its own
    // dark backdrop and always reads fine with light content).
    if (header.classList.contains(CLASSES.scrolled)) {
      header.classList.remove(CLASSES.lightBg);
      return;
    }

    // Manual override from the section settings wins over pixel-probing.
    var mode = header.getAttribute("data-overlay-contrast") || "auto";
    if (mode === "force_light") {
      header.classList.remove(CLASSES.lightBg);
      return;
    }
    if (mode === "force_dark") {
      header.classList.add(CLASSES.lightBg);
      return;
    }

    var light = backdropIsLight();
    header.classList.toggle(CLASSES.lightBg, light === true);
  }

  /**
   * Probe the element behind the header bar and decide if it's light.
   * Returns true / false, or null when it can't tell (image/video/gradient).
   */
  function backdropIsLight() {
    var rect = header.getBoundingClientRect();
    var y = Math.round(rect.bottom + 6);
    var xs = [0.5, 0.22, 0.78].map(function (f) {
      return Math.round(window.innerWidth * f);
    });

    var lums = [];

    for (var xi = 0; xi < xs.length; xi++) {
      var x = xs[xi];

      // Let elementFromPoint see through the header and its hover zone.
      var probe = document.elementsFromPoint
        ? document.elementsFromPoint(x, y)
        : [document.elementFromPoint(x, y)];

      for (var i = 0; i < probe.length; i++) {
        var el = probe[i];
        if (!el || el === document.documentElement || el === document.body)
          continue;
        if (
          el.closest(
            ".custom-header, .custom-header__hover-zone, .custom-header__drawer"
          )
        )
          continue;

        var tag = el.tagName;
        if (
          tag === "IMG" ||
          tag === "VIDEO" ||
          tag === "CANVAS" ||
          tag === "svg" ||
          tag === "PICTURE"
        ) {
          return null;
        }

        var cs = getComputedStyle(el);
        if (cs.backgroundImage && cs.backgroundImage !== "none") return null;

        // elementsFromPoint sees straight through `pointer-events: none`
        // elements — which is exactly what full-bleed hero photos use. If a
        // transparent element here has such an image/video sitting under it,
        // the real backdrop is imagery, not this element's background colour.
        if (hasCoveringMedia(el, x, y)) return null;

        var lum = colorLuminance(cs.backgroundColor);
        if (lum !== null) {
          lums.push(lum);
          break;
        }
      }
    }

    if (lums.length) {
      var avg =
        lums.reduce(function (a, b) {
          return a + b;
        }, 0) / lums.length;
      return avg > 0.62;
    }

    // Nothing opaque found — fall back to the page background.
    var pageLum = colorLuminance(
      getComputedStyle(document.body).backgroundColor
    );
    if (pageLum === null) {
      pageLum = colorLuminance(
        getComputedStyle(document.documentElement).backgroundColor
      );
    }
    // Shopify storefronts are light by default — assume light if still unknown.
    if (pageLum === null) return true;
    return pageLum > 0.62;
  }

  /**
   * True when `el` (or a descendant) is a visible image / picture / video
   * whose box covers the probe point — a background photo the pixel probe
   * would otherwise miss because it is pointer-events:none.
   */
  function hasCoveringMedia(el, x, y) {
    if (!el.querySelectorAll) return false;
    var media = el.querySelectorAll("img, picture, video");
    for (var i = 0; i < media.length; i++) {
      var m = media[i];
      if (
        m.closest(
          ".custom-header, .custom-header__hover-zone, .custom-header__drawer"
        )
      )
        continue;
      var r = m.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) continue;
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
      var mcs = getComputedStyle(m);
      if (mcs.visibility === "hidden" || mcs.display === "none") continue;
      if (parseFloat(mcs.opacity || "1") < 0.1) continue;
      return true;
    }
    return false;
  }

  /**
   * Relative luminance (0–1) of an rgb/rgba() string, or null if it's
   * transparent / unparseable.
   */
  function colorLuminance(color) {
    if (!color) return null;
    var m = color.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    // Handles both "rgb(0, 0, 0)" and the newer "rgb(0 0 0 / 0.5)" syntax.
    var p = m[1]
      .split(/[,/\s]+/)
      .filter(Boolean)
      .map(function (n) {
        return parseFloat(n);
      });
    var a = p.length > 3 ? p[3] : 1;
    if (a < 0.5) return null; // mostly transparent — treat as "can't tell"
    return (0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2]) / 255;
  }

  /**
   * Hide the header
   */
  function hideHeader() {
    if (isHidden) return;
    isHidden = true;
    isHoverRevealed = false;
    header.classList.add(CLASSES.hidden);
    header.classList.remove(CLASSES.hoverReveal);
  }

  /**
   * Show the header (full reveal, not hover)
   */
  function showHeader() {
    if (!isHidden) return;
    isHidden = false;
    isHoverRevealed = false;
    header.classList.remove(CLASSES.hidden, CLASSES.hoverReveal);
  }

  /**
   * Hover zone entered — reveal header temporarily
   */
  function onHoverZoneEnter() {
    if (!isHidden) return;
    isHoverRevealed = true;
    header.classList.add(CLASSES.hoverReveal);
  }

  /**
   * Header entered via hover — keep it visible
   */
  function onHeaderEnter() {
    if (isHidden && !isHoverRevealed) {
      isHoverRevealed = true;
      header.classList.add(CLASSES.hoverReveal);
    }
  }

  /**
   * Header left via hover — re-hide if still in hidden state
   */
  function onHeaderLeave() {
    if (isHidden && isHoverRevealed) {
      isHoverRevealed = false;
      header.classList.remove(CLASSES.hoverReveal);
    }
  }

  /**
   * Mega menu hover behavior with delay
   * Uses CSS class toggling (not inline styles) so mega menu
   * positioning rules in CSS are never overridden.
   */
  function initMegaMenus() {
    var menuItems = document.querySelectorAll(SELECTORS.menuItems);
    var HOVER_DELAY_IN = 80;
    var HOVER_DELAY_OUT = 250;

    menuItems.forEach(function (item) {
      var enterTimeout = null;
      var leaveTimeout = null;
      var megaMenu = item.querySelector(SELECTORS.megaMenu);

      if (!megaMenu) return;

      item.addEventListener("mouseenter", function () {
        clearTimeout(leaveTimeout);
        enterTimeout = setTimeout(function () {
          megaMenu.classList.add("is-open");
        }, HOVER_DELAY_IN);
      });

      item.addEventListener("mouseleave", function () {
        clearTimeout(enterTimeout);
        leaveTimeout = setTimeout(function () {
          megaMenu.classList.remove("is-open");
        }, HOVER_DELAY_OUT);
      });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
